// @greenlight-spec: 59-platform-agent.md#integration-tester
// Fixture data for the Greenlight Agents entry points on the Integrations tab.
// Preview only: nothing here is served by the control plane.
import type { TestVerdict, TranscriptEvent } from './Transcript';

const MIN = 60_000;
export const minutesAgo = (n: number): string => new Date(Date.now() - n * MIN).toISOString();

export const EMBEDDED_TEST_SESSION = {
  agentId: 'integration-tester',
  sessionId: 'sess_test_new',
} as const;

export const GATHER_KNOWLEDGE_SESSION = {
  agentId: 'knowledge-explorer',
  sessionId: 'sess_exp_new',
} as const;

export const sessionHref = (s: { agentId: string; sessionId: string }, target?: string): string =>
  `/agents/${s.agentId}/sessions/${s.sessionId}${target ? `?target=${encodeURIComponent(target)}` : ''}`;

export interface LastTest {
  verdict: TestVerdict;
  at: string;
  sessionId: string;
}

/** The latest Integration tester verdict per integration slug in the mock world. */
export const LAST_TEST_BY_SLUG: Record<string, LastTest> = {
  salesforce: { verdict: 'passed', at: minutesAgo(124), sessionId: 'sess_test_new' },
  snowflake: { verdict: 'passed', at: minutesAgo(26 * 60), sessionId: 'sess_test_snow' },
  hubspot: { verdict: 'passed_with_findings', at: minutesAgo(5 * 60), sessionId: 'sess_test_new' },
  netsuite: { verdict: 'failed', at: minutesAgo(30 * 60), sessionId: 'sess_test_sap' },
};

export function hostOf(baseUrl: string): string {
  try {
    return new URL(baseUrl).host;
  } catch {
    return baseUrl;
  }
}

/**
 * The Integration tester's dual-route run against one integration, ending in a
 * passed-with-findings report: the proxied route passes, the direct route cannot
 * leave the agent runtime, and the cross-diagnosis says why that is not the
 * integration's fault.
 */
export function integrationTestTranscript(
  name: string,
  baseUrl: string,
  by = 'you',
  credentialSlug = 'default',
): TranscriptEvent[] {
  const at = minutesAgo(2);
  const host = hostOf(baseUrl);
  return [
    {
      id: 't1',
      kind: 'system',
      at,
      text: `Started by ${by} for ${name}, credential ${credentialSlug}. Context: base URL ${baseUrl}, auth placement Authorization: Bearer. Expected output: a per-route report.`,
    },
    {
      id: 't2',
      kind: 'assistant',
      at,
      text: `I’ll run the contract test through the Greenlight proxy first, then make the same calls directly with the credential, and compare the two routes.`,
    },
    {
      id: 't3',
      kind: 'tool',
      at,
      tool: {
        name: 'inspectIntegrationApi',
        input: 'GET /api/v1/status',
        output: '200 OK · 412 ms\nAuthorization injected by the proxy (header)',
        ok: true,
      },
    },
    {
      id: 't4',
      kind: 'tool',
      at,
      tool: {
        name: 'inspectIntegrationApi',
        input: 'GET /api/v1/records?limit=5&fields=id,name',
        output: '200 OK · 5 rows · query parameters bound as sent',
        ok: true,
      },
    },
    {
      id: 't5',
      kind: 'tool',
      at,
      tool: {
        name: 'inspectIntegrationApi',
        input: 'GET /api/v1/records?limit=1000 (follow cursor)',
        output:
          '3 pages · 2,000 rows returned · result cap applied at 2,000\nnext cursor honoured on every page',
        ok: true,
      },
    },
    {
      id: 't6',
      kind: 'tool',
      at,
      tool: {
        name: 'inspectIntegrationApi',
        input: 'GET /api/v1/does-not-exist',
        output: '404 Not Found · upstream error passed through unchanged',
        ok: true,
      },
    },
    {
      id: 't7',
      kind: 'tool',
      at,
      tool: {
        name: 'inspectIntegrationApi',
        input: 'POST /api/v1/records (expect denial)',
        output: '403 from Greenlight · write methods are not granted on this integration',
        ok: true,
      },
    },
    {
      id: 't8',
      kind: 'assistant',
      at,
      text: 'The proxied route passes every check. Now the direct route: the raw credential against the upstream with the registered header placement.',
    },
    {
      id: 't9',
      kind: 'tool',
      at,
      tool: {
        name: 'Bash',
        input: `curl -sS -H "Authorization: Bearer $CREDENTIAL" https://${host}/api/v1/status`,
        output: 'curl: (28) Connection timed out after 10001 milliseconds',
        ok: false,
      },
    },
    {
      id: 't10',
      kind: 'tool',
      at,
      tool: {
        name: 'getLogs',
        input: `{ component: "proxy", host: "${host}", since: "5m" }`,
        output: '5 requests · 200, 200, 200, 404, 403 · egress through the proxy',
        ok: true,
      },
    },
    {
      id: 't11',
      kind: 'assistant',
      at,
      text: `The proxy reaches ${host} and the credential works there, so neither the credential nor this integration’s configuration is at fault. The direct timeout is the agent runtime’s own network path: it has no route to this host. Apps never take that path.`,
    },
    {
      id: 't12',
      kind: 'report',
      at,
      report: {
        verdict: 'passed_with_findings',
        routes: [
          {
            name: 'Proxied route',
            ok: true,
            checks: [
              { label: 'Sanity call', ok: true, detail: '200 in 412 ms' },
              { label: 'Parameter binding', ok: true, detail: '5 rows, fields as asked' },
              { label: 'Paging', ok: true, detail: '3 pages, cursor followed' },
              { label: 'Result cap', ok: true, detail: 'applied at 2,000 rows' },
              { label: 'Induced error', ok: true, detail: '404 passed through' },
              { label: 'Denied write', ok: true, detail: '403 from Greenlight' },
            ],
          },
          {
            name: 'Direct route',
            ok: false,
            checks: [
              { label: 'Sanity call', ok: false, detail: 'timed out after 10 s' },
              { label: 'Credential', ok: null, detail: 'not reached' },
            ],
          },
        ],
        diagnosis: `Proxied passes, direct fails: an egress finding, not a configuration or credential problem. Apps reach ${host} through the proxy, which works.`,
        fix: `Nothing to change for apps. For direct-route evidence next time, allow ${host}:443 from the agent runtime.`,
      },
    },
    {
      id: 't13',
      kind: 'assistant',
      at,
      text: 'Report filed. Ask a follow-up, or tell me to re-run once the direct path is open.',
    },
  ];
}

/** The Knowledge explorer's opening moves on one integration, cut off mid-run. */
export function knowledgeExploreTranscript(name: string): TranscriptEvent[] {
  const at = minutesAgo(4);
  return [
    {
      id: 'k1',
      kind: 'system',
      at,
      text: `Started by you with Gather knowledge on ${name}. Context: the integration snapshot, 3 accepted Knowledge entries, the goal: what an app builder needs to know.`,
    },
    {
      id: 'k2',
      kind: 'assistant',
      at,
      text: `I’ll start with metadata — what this credential can see — before reading any rows, then check the vendor’s docs for paging and rate limits.`,
    },
    {
      id: 'k3',
      kind: 'tool',
      at,
      tool: {
        name: 'knowledgeList',
        input: `{ integration: "${name}", status: "accepted" }`,
        output: '3 entries: auth, base URL, field naming',
        ok: true,
      },
    },
    {
      id: 'k4',
      kind: 'tool',
      at,
      tool: {
        name: 'inspectIntegrationApi',
        input: 'GET /api/v1/metadata/objects',
        output: '200 · 41 objects visible to this credential · 6 writable (not probed)',
        ok: true,
      },
    },
    {
      id: 'k5',
      kind: 'tool',
      at,
      tool: {
        name: 'WebFetch',
        input: `developer docs · ${name} REST pagination`,
        output: 'Cursor paging; page size max 2,000; nextRecordsUrl is relative',
        ok: true,
      },
    },
    {
      id: 'k6',
      kind: 'assistant',
      at,
      text: 'The docs say page size caps at 2,000 and the next-page URL is relative. I’ll confirm that against one sampled read, then draft entries.',
    },
    {
      id: 'k7',
      kind: 'tool',
      at,
      tool: {
        name: 'inspectIntegrationApi',
        input: 'GET /api/v1/records?limit=3 (sample)',
        output: '200 · 3 rows · nextRecordsUrl: /api/v1/records?cursor=…',
        ok: true,
      },
    },
    {
      id: 'k8',
      kind: 'tool',
      at,
      tool: {
        name: 'knowledgeSearch',
        input: '"pagination" · "relative URL"',
        output: 'no accepted entry covers this',
        ok: true,
      },
    },
  ];
}
